import { Injectable, inject } from '@angular/core';
import { signIn, signOut, fetchAuthSession } from '@aws-amplify/auth';
import * as Sentry from '@sentry/angular';
import { map, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { IoTClientService } from '../common/iot-client.service';
import { firstValueFrom, Observable, of } from 'rxjs';
import { UserRole } from '@core/enums/user-role.enum';
import { UserSignalStore } from '@core/stores/user-signal-store.service';
import { CompanyApiService } from '../api/company-api.service';
import { PermissionsService } from './permissions.service';
import { RolesService } from '../../../permission/services/roles.service';
import { LocalStorageService } from '../common/local-storage.service';
import { LocalStorageKeyEnum } from '../../enums/local-storage-key.enum';
import { UserInterface } from '../../interfaces/user.interface';
import { environment } from 'src/environments/environment';
import { CookieStorage, sessionStorage } from 'aws-amplify/utils';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { UserApiService } from '@core/services/api/user-api.service';
import { CompanyContextService } from './company-context.service';

// Expected state for logged-out visitors — must not be logged via console.error,
// because captureConsoleIntegration reports those to Sentry on production/staging.
const NO_SESSION_ERROR = 'No current session';

export type LoginParams = {
  email: string;
  password: string;
  keepLoggedIn: string;
};

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private router = inject(Router);
  private iotClientService = inject(IoTClientService);
  private userStore = inject(UserSignalStore);
  private userApiService = inject(UserApiService);
  private companyApiService = inject(CompanyApiService);
  private companyContextService = inject(CompanyContextService);
  private permissionsService = inject(PermissionsService);
  private rolesService = inject(RolesService);
  private localStorage = inject(LocalStorageService);

  public async initialize() {
    try {
      this.configAuthStorage();

      const cognitoGroups = await this.getCognitoGroups();

      if (cognitoGroups.includes(UserRole.Machine)) return await signOut();

      if (cognitoGroups.includes(UserRole.Pilot)) {
        window.location.href = environment.toolsAppUrl;
      }
      await this.refreshCurrentUser(cognitoGroups);
    } catch (error) {
      if (error instanceof Error && error.message === NO_SESSION_ERROR) {
        console.debug('Authentication skipped: no active session');
      } else {
        console.error('Authentication failed: ', error);
      }
    }
  }

  public async logInMachineUser(options: LoginParams): Promise<void> {
    try {
      this.cleanup();
      await signOut();
      if (this.localStorage.getItem(LocalStorageKeyEnum.KeepLoggedIn) !== options.keepLoggedIn) {
        this.localStorage.setItem(LocalStorageKeyEnum.KeepLoggedIn, options.keepLoggedIn);
        this.configAuthStorage();
      }

      await signIn({ username: options.email, password: options.password });

      console.debug('Session created');
    } catch (err) {
      console.error('Log-in failed:', err);
      throw err;
    }
  }

  public async logIn(options: LoginParams): Promise<void> {
    try {
      this.cleanup();
      await signOut();
      if (this.localStorage.getItem(LocalStorageKeyEnum.KeepLoggedIn) !== options.keepLoggedIn) {
        this.localStorage.setItem(LocalStorageKeyEnum.KeepLoggedIn, options.keepLoggedIn);
        this.configAuthStorage();
      }

      await signIn({ username: options.email, password: options.password });

      const cognitoGroups = await this.getCognitoGroups();

      if (cognitoGroups.includes(UserRole.Pilot)) {
        window.location.href = environment.toolsAppUrl;
      }

      await this.refreshCurrentUser(cognitoGroups);

      if (this.iotClientService.isEnabled) {
        const { identityId } = await fetchAuthSession();
        if (identityId) {
          await this.iotClientService.attachIoTPolicy(identityId);
        }
      }

      console.debug('Session created');
    } catch (err) {
      console.error('Log-in failed:', err);
      throw err;
    }
  }

  public async logOut(): Promise<boolean> {
    try {
      this.cleanup();
      await signOut();
      Sentry.setUser(null);

      return this.router.navigate(['/user/login']);
    } catch (err: any) {
      console.error('Log-out failed:', err);
      throw new Error(err);
    }
  }

  public async getJwtAccessToken() {
    try {
      const { tokens } = await fetchAuthSession();

      return tokens?.accessToken;
    } catch {
      try {
        const { tokens } = await fetchAuthSession({ forceRefresh: true });

        return tokens?.accessToken;
      } catch (err) {
        console.error('Token retrieval failed:', err);

        return null;
      }
    }
  }

  public async getJwtIdToken() {
    try {
      const { tokens } = await fetchAuthSession();

      return tokens?.idToken;
    } catch (err) {
      console.error('Token retrieval failed:', err);

      return null;
    }
  }

  public async refreshCurrentUser(cognitoGroups: UserRole[]) {
    try {
      const currentUser = await firstValueFrom(this.getCurrentUser(cognitoGroups));
      this.userStore.setCurrentUser(currentUser);
      this.permissionsService.setRolesAndPermissions(currentUser.data);
      Sentry.setUser(currentUser.data);
    } catch (error) {
      console.error('Error while refreshing current user: ', error);
    }
  }

  public async getCognitoGroups(): Promise<UserRole[]> {
    const jwt = await this.getJwtAccessToken();
    if (!jwt) {
      throw new Error(NO_SESSION_ERROR);
    }
    const cognitoGroups = jwt.payload?.['cognito:groups'] as UserRole[];

    return cognitoGroups;
  }

  private async configAuthStorage() {
    const storage =
      this.localStorage.getItem(LocalStorageKeyEnum.KeepLoggedIn) !== 'true'
        ? sessionStorage
        : new CookieStorage(environment.awsConfig.auth.cookieStorage);

    cognitoUserPoolsTokenProvider.setKeyValueStorage(storage);
  }

  private cleanup() {
    this.rolesService.flushRolesAndPermissions();
  }

  private getCurrentUser(cognitoGroups: UserRole[]): Observable<{ data: UserInterface }> {
    return this.userApiService.fetchCurrentUser().pipe(
      map((user) => {
        const role = this.getUserRole(cognitoGroups);
        if (role) {
          user.data.userRole = role;
        }

        return user;
      }),
      switchMap((user) => {
        const companyId = user.data.contact_profile?.company;

        if (!companyId) {
          return of(user);
        }

        return this.companyApiService.getSingleCompany(companyId).pipe(
          map((response) => {
            this.companyContextService.setCompany(response.data);
            user.data.company_profile = response.data;

            return user;
          }),
        );
      }),
    );
  }

  private getUserRole(cognitoGroups: UserRole[]): UserRole | undefined {
    const roles = new Set(cognitoGroups);

    if (roles.has(UserRole.SuperAdmin)) {
      return UserRole.SuperAdmin;
    }

    if (roles.has(UserRole.CompanyUser)) {
      if (roles.has(UserRole.CompanyAdmin)) {
        return UserRole.CompanyAdmin;
      }

      return UserRole.CompanyUser;
    }

    Sentry.logger.error('User does not have a valid role', { cognitoGroups });

    return undefined;
  }
}
